import { Select } from "src/components/Select/Select.tsx";
import { Flex } from "src/components/Flex.tsx";
import {
  gridFilterModelSelector,
  type GridRenderHeaderFilterProps,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import type { SelectProps } from "src/components/Select/Select.tsx";
import type {
  HeaderFilterSelectOptions,
  HeaderFilterSelectServerOptions,
} from "src/components/DataGrid/DataGrid.tsx";

import { useSelect } from "src/system/useSelect.tsx";
import { arrReplaceAt } from "@exonn/utils";
import { useDataGridStore } from "src/components/DataGrid/DataGridStoreProvider.tsx";
import { observer } from "mobx-react-lite";
import { mergeFilterModels } from "src/components/DataGrid/mergeFilterModels.ts";
import { useGridRootProps } from "@mui/x-data-grid-pro/hooks/utils/useGridRootProps";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { getResourceFieldOptions } from "src/system/getResourceFieldOptions.ts";
import { getDefaultSelectOptions } from "src/system/getDefaultSelectOptions.ts";

export const DataGridMultiSelectHeaderFilter = observer(
  (
    props: GridRenderHeaderFilterProps &
      Pick<SelectProps<string>, "placeholder" | "allowClearValue"> & {
        options?: HeaderFilterSelectOptions;
        serverOptions?: HeaderFilterSelectServerOptions;
      },
  ) => {
    const {
      item,
      options: optionsProp,
      placeholder,
      allowClearValue,
      serverOptions: serverOptionsProp,
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const apiRef = useGridApiContext();
    const { t } = useTranslation(TranslationNamespaceEnum.DataGridComponent);
    const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
    const store = useDataGridStore();
    const appliedItem = store.appliedFilterModel.items.find(
      (a) => a.field === item.field,
    );
    const rootProps = useGridRootProps();

    // const applyFilter = useCallback(
    //   (updatedItem: GridFilterItem) => {
    //     if (
    //       Array.isArray(updatedItem.value) &&
    //       updatedItem.value.length === 0
    //     ) {
    //       apiRef.current.deleteFilterItem(updatedItem);
    //     } else if (!updatedItem.value) {
    //       apiRef.current.deleteFilterItem(updatedItem);
    //     } else {
    //       apiRef.current.upsertFilterItem(updatedItem);
    //     }
    //   },
    //   [apiRef],
    // );

    const getDisplayValue = () => {
      if (item.value != null) {
        return item.value;
      }
      if (appliedItem?.value != null) {
        return appliedItem.value;
      }

      return [];
    };

    const value = getDisplayValue();
    const hasValue = !!value?.length;

    const serverOptions = useMemo(() => {
      if (serverOptionsProp) {
        return {
          ...getDefaultSelectOptions({
            resource: serverOptionsProp.resource,
          }),
          ...serverOptionsProp,
        };
      }
      return undefined;
    }, [serverOptionsProp]);

    const {
      options: backendOptions,
      query: { isLoading: isLoadingServerOptions },
    } = useSelect({
      resource: serverOptions ? serverOptions.resource : "",
      labelProp: serverOptions?.labelProp,
      valueProp: serverOptions?.valueProp,
      include: serverOptions?.include,
      disablePagination: true,
      filter: serverOptions?.filter
        ? {
            items: serverOptions?.filter,
          }
        : undefined,
      queryOptions: {
        enabled: !!serverOptions && (isOpen || hasValue),
      },
    });

    const handleSelect = (value?: string[] | "") => {
      const nextItem = {
        ...item,
        value: value,
      };
      const mergedFilterModel = mergeFilterModels(
        filterModel,
        store.appliedFilterModel,
      );
      const prevItems = mergedFilterModel.items;

      const nextItems = arrReplaceAt(
        prevItems,
        (prevItem) => prevItem.field === nextItem.field,
        nextItem,
        true,
      );

      const filteredItems = nextItems.filter((item) => {
        return Array.isArray(item.value)
          ? item.value.length !== 0
          : item.value != null && item.value !== "";
      });
      const nextFilterModel = {
        ...mergedFilterModel,
        items: filteredItems,
      };
      if (rootProps.filterMode === "server") {
        apiRef.current.setFilterModel({
          items: [],
        });
        // applyFilter(nextItem);
        store.setAppliedFilterModel(nextFilterModel);
      } else {
        apiRef.current.setFilterModel(nextFilterModel);
      }
    };

    const options = serverOptions
      ? backendOptions
      : Array.isArray(optionsProp)
      ? optionsProp
      : optionsProp &&
        getResourceFieldOptions({
          ...optionsProp,
          t,
        });

    return (
      <Flex style={styles.container}>
        <Select<string>
          truncateOptions={true}
          isMultiple={true}
          allowClearValue={allowClearValue}
          onChange={handleSelect}
          onOpenChange={(open) => setIsOpen(open)}
          isLoadingData={isLoadingServerOptions}
          isLoadingSelectedOption={isLoadingServerOptions}
          placeholder={placeholder ?? t("header-filters.select-placeholder")}
          value={value}
          options={options}
        />
      </Flex>
    );
  },
);

const styles = {
  container: {
    // 1px padding is needed to not cut off input outline
    padding: "1px 10px 10px",
    width: "100%",
    height: "100%",
  },
};
